'use client';

import { TeamLeagueCardv2 } from '@pickleballinc/features/src/global/cards/TeamLeagueCardv2';
import { formatLocation } from '@pickleballinc/lib/format-location';
import { Session } from '@pickleballinc/lib/session';
import { PaginationV2 } from '@pickleballinc/react-ui';
import { TeamLeagueCard } from '@pickleballinc/ui/components/global/Cards/TeamLeagueCard';
import { MyModulesNoResults } from '@pickleballinc/ui/components/global/myModules/MyModulesNoResults';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';

import { useTeamLeagueFavorite } from '@/hooks/tourney/useFavoriteToggle';
import { publicEnv } from '@/lib/env';
import AccessLimited from '@/modules/players/v2/tabs/components/AccessLimited';
import { ALL_MODULES_PAGE_SIZE, TEAM_LEAGUE_MODULE_FOR } from '@/modules/players/v2/tabs/constants';
import { getNoResultsConfig } from '@/modules/players/v2/tabs/helpers';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { PlayerData } from '@/types/players.types';
import { TeamLeagueDto, teamLeagueTypeMap, TeamLeagueTypes } from '@/types/playerTeamLeagues.types';
import { useFavoriteLeagueMutation } from '@/utils/actions/leagues/useFavoriteLeagueMutation';
import { FEATURE_FLAGS } from '@/utils/helpers/constants';

interface PlayerTeamLeaguesWrapperProps {
  teamLeagues: TeamLeagueDto[];
  player: PlayerData | undefined;
  user?: Session;
  totalRecords: number;
}

export const PlayerTeamLeaguesWrapper = ({
  teamLeagues,
  player,
  user,
  totalRecords,
}: PlayerTeamLeaguesWrapperProps) => {
  const router = useRouter();

  const { hasPermission } = useAuth();
  const isPickleballSuperAdmin = hasPermission(Permission.SUPER_ADMIN);
  const isPBSuperAdmin = hasPermission(Permission.PICKLEBALL_BRACKETS_ADMIN);
  const isSuperAdmin = isPBSuperAdmin || isPickleballSuperAdmin;
  const isUserAlsoPlayer = user?.uuid.toLowerCase() === player?.uuid.toLowerCase();
  const [leaguesData, setLeaguesData] = useState(teamLeagues || []);
  const [activeFavoriteUuid, setActiveFavoriteUuid] = useState<string | null>(null);
  const { addToFavorites, removeFromFavorites, isAdding, isRemoving } = useFavoriteLeagueMutation();

  const searchParams = useSearchParams();
  const page = Number(searchParams?.get('page') || 1);
  const typeForTeamLeague = searchParams?.get('team_league_type') || 'registeredfor';
  const activeTeamLeagues = searchParams?.get('status') || 'future';

  const currentTeamLeagueType = teamLeagueTypeMap[typeForTeamLeague];

  const isManage = currentTeamLeagueType === TeamLeagueTypes.Managing;
  const isRegistered = currentTeamLeagueType === TeamLeagueTypes.RegisteredFor;
  const isFavorited = currentTeamLeagueType === TeamLeagueTypes.Favorited;

  const canManageTeamLeague = isManage && (isUserAlsoPlayer || isSuperAdmin);

  const hasTeamLeagues = teamLeagues?.length > 0;

  const handlePageChange = (newPage: number) => {
    router.replace(
      `?status=${activeTeamLeagues}&team_league_type=${typeForTeamLeague}&page=${newPage}`
    );
  };

  const handleFavoriteToggle = (event: React.MouseEvent, isFavorite: boolean, uuid: string) => {
    event.stopPropagation();
    event.preventDefault();

    const mutation = isFavorite ? removeFromFavorites : addToFavorites;
    if (isAdding || isRemoving) return;

    setActiveFavoriteUuid(uuid);

    setLeaguesData((prev) =>
      prev.map((league) =>
        league.uuid === uuid ? { ...league, isFavourite: !isFavorite } : league
      )
    );

    mutation.mutate(
      { moduleUuid: uuid, moduleFor: TEAM_LEAGUE_MODULE_FOR },
      {
        onSuccess: () => {
          if (isFavorite) {
            setLeaguesData((prev) => prev.filter((l) => l.uuid !== uuid));
          } else {
            setLeaguesData((prev) =>
              prev.map((league: TeamLeagueDto) =>
                league.uuid === uuid ? { ...league, isFavourite: true } : league
              )
            );
          }
        },
        onError: () => {
          setLeaguesData((prev) =>
            prev.map((league) =>
              league.uuid === uuid ? { ...league, isFavourite: isFavorite } : league
            )
          );
        },
        onSettled: () => {
          setActiveFavoriteUuid(null);
        },
      }
    );
  };

  const noResultsConfig = getNoResultsConfig({
    moduleType: 'teamLeague',
    isManage,
    isRegistered,
    player,
    isUserAlsoPlayer,
  });

  const totalPages = Math.ceil(totalRecords / ALL_MODULES_PAGE_SIZE);

  return (
    <div className='flex flex-col items-center gap-6 sm:gap-10 justify-center'>
      {hasTeamLeagues && (isRegistered || canManageTeamLeague || isFavorited) ? (
        <>
          <div className='mx-auto !max-w-7xl grid grid-cols-1 min-[500px]:grid-cols-2 gap-3 py-4 md:grid-cols-3 md:gap-10 lg:grid-cols-4 w-full'>
            {leaguesData.map((teamLeague) =>
              FEATURE_FLAGS.ENABLE_NEW_CARDS ? (
                <TeamLeagueCardv2
                  key={`team-league-${teamLeague.uuid}`}
                  {...teamLeague}
                  id={teamLeague.uuid}
                  logo={teamLeague.logo?.url}
                  location={
                    teamLeague.location
                      ? formatLocation([
                          teamLeague.location.city,
                          teamLeague.location.stateInfo.title,
                          teamLeague.location.countryInfo.abbreviation,
                        ])
                      : undefined
                  }
                  teamLeaguesBaseUrl={`${publicEnv.NEXT_PUBLIC_PTL_URL}`}
                  useFavoriteState={useTeamLeagueFavorite}
                  showFavoriteIcon={!isFavorited}
                  initialIsFavorite={teamLeague.isFavourite}
                />
              ) : (
                <TeamLeagueCard
                  key={`team-league-${teamLeague.uuid}`}
                  league={teamLeague}
                  teamLeaguesBaseUrl={publicEnv.NEXT_PUBLIC_PTL_URL}
                  isUserLogged={!!user?.uuid}
                  isUserAlsoPlayer={isUserAlsoPlayer}
                  handleFavoriteToggle={(e) =>
                    handleFavoriteToggle(e, teamLeague.isFavourite, teamLeague.uuid)
                  }
                  isAddingLeagueToFavorite={activeFavoriteUuid === teamLeague.uuid}
                  isFavoritedTab={isFavorited}
                />
              )
            )}
          </div>

          {totalPages >= 2 && (
            <PaginationV2
              totalNumberOfPages={totalPages}
              currentPage={page}
              onPreviousClick={() => handlePageChange(page - 1)}
              onNextClick={() => handlePageChange(page + 1)}
              onPageClick={(newPage) => handlePageChange(newPage)}
            />
          )}
        </>
      ) : !canManageTeamLeague && isManage ? (
        <AccessLimited />
      ) : (
        <div className='mx-auto max-w-[442px]'>
          <MyModulesNoResults {...noResultsConfig} />
        </div>
      )}
    </div>
  );
};
